import { useEffect, useState } from "react";
import { Eye, FileText, Mail, Users } from "lucide-react";
import { api } from "@/lib/api";

const PAGE_LABELS = {
  "/": "Home",
  "/chi-siamo": "Chi Siamo",
  "/servizi": "Servizi",
  "/team": "Team",
  "/insights": "Insights",
  "/contatti": "Contatti",
  "/privacy": "Privacy",
};

export default function AdminStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (stats === null) {
    return <p className="card-surface rounded-2xl p-10 text-center font-mono text-sm text-slate-500" data-testid="stats-loading">Caricamento statistiche…</p>;
  }
  if (stats === false) {
    return <p className="card-surface rounded-2xl p-10 text-center text-sm text-red-300" data-testid="stats-error">Statistiche non disponibili.</p>;
  }

  const maxDay = Math.max(1, ...stats.by_day.map((d) => d.views));
  const maxPage = Math.max(1, ...stats.by_page.map((p) => p.views));

  const cards = [
    { icon: Eye, label: "Visite ultimi 30 giorni", value: stats.views_30d, testid: "stats-views-30d" },
    { icon: Eye, label: "Visite totali", value: stats.total_views, testid: "stats-views-total" },
    { icon: Users, label: "Visitatori unici", value: stats.unique_visitors, testid: "stats-unique" },
    { icon: Mail, label: "Richieste di contatto", value: stats.contacts_total, testid: "stats-contacts" },
    { icon: FileText, label: "Iscrizioni newsletter", value: stats.newsletter, testid: "stats-newsletter" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-stats-panel">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map((c) => (
          <div key={c.testid} className="card-surface rounded-2xl p-5" data-testid={c.testid}>
            <c.icon size={18} className="text-gold" />
            <p className="mt-3 font-serif text-3xl text-slate-100">{c.value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] leading-relaxed text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="card-surface rounded-2xl p-7" data-testid="stats-chart">
        <h3 className="font-serif text-xl text-slate-100">Visite — ultimi 30 giorni</h3>
        <div className="mt-6 flex h-36 items-end gap-1">
          {stats.by_day.map((d) => (
            <div
              key={d.day}
              title={`${new Date(d.day).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}: ${d.views} visite`}
              className="flex-1 rounded-t bg-gradient-to-t from-gold-dark/60 via-gold/70 to-gold-light transition-all hover:from-gold-dark hover:to-gold"
              style={{ height: `${Math.max(2, (d.views / maxDay) * 100)}%` }}
              data-testid={`stats-bar-${d.day}`}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between font-mono text-[10px] text-slate-600">
          <span>{new Date(stats.by_day[0].day).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</span>
          <span>{new Date(stats.by_day[stats.by_day.length - 1].day).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</span>
        </div>
      </div>

      <div className="card-surface rounded-2xl p-7" data-testid="stats-by-page">
        <h3 className="font-serif text-xl text-slate-100">Visite per pagina</h3>
        <ul className="mt-5 space-y-3.5">
          {stats.by_page.length === 0 && (
            <li className="text-sm text-slate-500">Nessuna visita registrata finora.</li>
          )}
          {stats.by_page.map((p) => (
            <li key={p.path} data-testid={`stats-page-${p.path.replace(/\//g, "") || "home"}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{PAGE_LABELS[p.path] || p.path}</span>
                <span className="font-mono text-xs text-gold">{p.views}</span>
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-gold/80" style={{ width: `${(p.views / maxPage) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-6 border-t border-white/10 pt-4 text-xs font-light text-slate-500">
          Le visite sono conteggiate solo per i visitatori che hanno accettato i cookie di analisi, in forma anonima.
        </p>
      </div>
    </div>
  );
}
